import { useObserve } from "expo-observe";
import { useEffect, useRef } from "react";

/**
 * Tell EAS Observe the screen is ready for the operator to act on (TRO-433).
 *
 * Observe times a cold start on its own, but only this app knows when the first screen stops being
 * a skeleton: the launch is done when the vendor can read their numbers or the customer can read
 * the pull sheet, not when React mounts. `ready` is therefore each screen's own answer (its query
 * settling, Clerk resolving, a fetch landing).
 *
 * Pass a settled signal, not a successful one. A screen that resolved into an error state is still
 * interactive, because the retry button is there to press. Treating failure as never interactive
 * would drop the sessions most worth measuring and bias the metric toward the happy path.
 *
 * Safe to call from several screens in one session: `markInteractive` records only the first call,
 * so a `ready` that turns true late, twice, or on a screen reached by navigation costs nothing.
 *
 * Which routes need this follows from structure. Every route outside the `(app)` group is public
 * and deep-linkable, which is why those routes are siblings of `(app)` rather than children (see
 * `app/_layout.tsx`); a QR scan or shared link cold launches straight into one, so each needs this
 * hook. Inside `(app)`, only the two launch entries need it: `(tabs)/index` signed in and
 * `(auth)/sign-in` signed out. A route reached only by navigation does not; per-route navigation
 * timing comes from the `expo-router` integration configured in `app/_layout.tsx`.
 *
 * A public route without this hook still reports sessions and crashes, but never reports a
 * time-to-interactive, so its launches silently drop out of the startup percentiles.
 */
export function useMarkInteractive(ready: boolean) {
  const { markInteractive } = useObserve();
  const markInteractiveRef = useRef(markInteractive);
  markInteractiveRef.current = markInteractive;

  useEffect(() => {
    if (ready) markInteractiveRef.current();
  }, [ready]);
}
